import { PLANS } from "./types/entities.js";
export const CAPABILITIES = [
    "reports.view",
    "reports.export",
    "scripts.inventory",
    "scripts.pages",
    // Payment pages on a site, the filters and alert scope that use them (task
    // 306). Enforced by the API, not only the dashboard.
    "pages.payment",
    // Script authorization (task 307): decisions with their justification and
    // history, the review queue and the script alert types. Built on payment
    // pages, so it starts on the same plan. Reading the inventory, decisions and
    // export is `scripts.inventory`, or any tenant that has made a decision, so a
    // downgrade never locks away evidence.
    "scripts.authorization",
    // SAML single sign-on against a tenant's own identity provider (task 348).
    // The Business plan and above since task 429. Task 348 kept it to
    // Enterprise because Descope caps SSO connections per project (three on
    // its free plan); at the Business plan's price that cost is covered, and
    // the cap is lifted by moving Descope to a paid plan as customers arrive
    // (Michael, 2026-09-26). Until then a fourth connection is refused with
    // the 503 and operator log line in apps/dashboard-api/src/lib/descope-sso.ts,
    // and the operations runbook says what to do.
    "auth.sso",
    // A report host the tenant chose, `acme-shop.ingest.us.headerhawk.com`,
    // beside the generated code's (task 421). Claiming or changing one needs
    // this; serving one does not, so a downgrade never breaks a header the
    // customer published. It costs nothing to serve, and it is the plan a
    // compliance buyer, whose headers someone else reads, is on.
    "ingest.custom_label",
    // A report host on the customer's own domain, `csp.acme.com`, which they
    // point at us with one CNAME (task 422). Business and above, decided
    // 2026-09-25: each host is a DNS conversation with the customer's own team.
    // Unlike a label, losing it starts 30 days' grace and then suspends the
    // host, because a host has a per-host cost and a certificate we renew.
    "ingest.custom_host",
    // Reading the tenant's data from its own code or an AI assistant, rather
    // than the dashboard (ADR 014, the API and MCP access epic). Gates creating
    // an API key (task 437), using one (task 438) and consenting to the MCP
    // server (task 440). Losing it suspends every live key rather than deleting
    // it, so a customer who pays again does not reissue them.
    "api.access",
    // Alert destinations beyond email (task 395): Slack and webhooks, the
    // Team plan and above, as the pricing table sells them. Two rather than one
    // because the table lists them as two rows. Creating, editing and testing one
    // needs it, and so does delivery: a downgraded tenant keeps the destination,
    // which is silent until an upgrade brings it back.
    "destinations.slack",
    "destinations.webhook",
];
/**
 * What issuing, using and consenting to API access needs (task 437, ADR 014):
 * one name for the API, the dashboard and the plan reconciliation.
 */
export const API_ACCESS_CAPABILITY = "api.access";
/**
 * What a destination type needs (task 395). Email has no entry: every plan
 * delivers to it.
 */
export const DESTINATION_CAPABILITIES = {
    slack: "destinations.slack",
    webhook: "destinations.webhook",
};
const PLAN_CAPABILITIES = {
    free: ["reports.view", "reports.export"],
    team: [
        "reports.view",
        "reports.export",
        "scripts.inventory",
        "destinations.slack",
        "destinations.webhook",
    ],
    professional: [
        "reports.view",
        "reports.export",
        "scripts.inventory",
        "scripts.pages",
        "pages.payment",
        "scripts.authorization",
        "ingest.custom_label",
        "api.access",
        "destinations.slack",
        "destinations.webhook",
    ],
    // The plan the API-test and fuzz tenant is on. A capability left off it
    // needs its operations turned off in the fuzz run, or their 403s fail it as
    // `missing_auth` (task 389, `tests/capabilities.test.ts`).
    business: [
        "reports.view",
        "reports.export",
        "scripts.inventory",
        "scripts.pages",
        "pages.payment",
        "scripts.authorization",
        "ingest.custom_label",
        "auth.sso",
        "ingest.custom_host",
        "api.access",
        "destinations.slack",
        "destinations.webhook",
    ],
    // Every capability, always. A contract negotiates limits, not features:
    // per-tenant capability overrides are deliberately not a thing (task 345),
    // so an Enterprise tenant is defined by having all of them.
    enterprise: [
        "reports.view",
        "reports.export",
        "scripts.inventory",
        "scripts.pages",
        "pages.payment",
        "scripts.authorization",
        "auth.sso",
        "ingest.custom_label",
        "ingest.custom_host",
        "api.access",
        "destinations.slack",
        "destinations.webhook",
    ],
};
export function hasCapability(plan, capability) {
    return PLAN_CAPABILITIES[plan].includes(capability);
}
/**
 * The least plan that includes a capability: what a page names when it says
 * "on Professional" rather than showing a lock (ADR 013, principle 3).
 */
export function lowestPlanWith(capability) {
    const plan = PLANS.find((candidate) => hasCapability(candidate, capability));
    // Enterprise holds every capability, so there is always one.
    return plan ?? "enterprise";
}
/**
 * Whether a plan delivers to a destination type (task 395): what the
 * destination routes, the Slack install and alert delivery all ask, so they
 * cannot disagree. Email is always true.
 */
export function mayDeliverTo(plan, type) {
    return (type === "email" || hasCapability(plan, DESTINATION_CAPABILITIES[type]));
}
//# sourceMappingURL=capabilities.js.map